import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { ReadAloudButton } from "@/components/ReadAloudButton";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { playCorrect, playWrong, playWin, getMuted, toggleMuted } from "@/lib/sfx";
import { ArrowLeft, Gamepad2, Layers, Timer, Trophy, RotateCw, Sparkles, ChevronLeft, SpellCheck, PenLine, Send, Volume2, VolumeX } from "lucide-react";

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const pick = (arr, n) => shuffle(arr).slice(0, n);

export default function GamesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("hub");
  const [muted, setMuted] = useState(getMuted());

  useEffect(() => { api.get("/games/data").then((r) => setData(r.data)); }, []);

  // Record the play as soon as a game is opened so the "Game On" badge unlocks
  // even if the student exits early.
  useEffect(() => {
    if (view === "match") api.post("/games/play", { game: "vocab_match", score: 0, total: 0 }).catch(() => {});
    if (view === "quiz") api.post("/games/play", { game: "timed_quiz", score: 0, total: 0 }).catch(() => {});
    if (view === "spelling") api.post("/games/play", { game: "spelling_bee", score: 0, total: 0 }).catch(() => {});
    if (view === "fillblank") api.post("/games/play", { game: "fill_blank", score: 0, total: 0 }).catch(() => {});
  }, [view]);

  const hasVocab = data && data.vocab.length >= 3;
  const hasQuiz = data && data.quiz.length >= 2;
  const spellingPool = data ? (data.spelling || data.vocab) : [];
  const hasSpelling = spellingPool.length >= 3;

  return (
    <div>
      <TopBar subtitle="Mini-Games" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => view === "hub" ? navigate("/student") : setView("hub")}
          className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6">
          <ArrowLeft size={16} /> {view === "hub" ? "Dashboard" : "Back to games"}
        </button>

        {!data && <p className="text-[#6B7280]">Loading games…</p>}

        {data && view === "hub" && (
          <>
            <div className="flex justify-end mb-3">
              <button data-testid="sfx-toggle" onClick={() => setMuted(toggleMuted())}
                className="flex items-center gap-2 rounded-full border border-[#EAE6DF] bg-white px-4 py-2 text-sm font-medium text-[#3D405B] hover:border-[#F4A261] transition-colors">
                {muted ? <VolumeX size={15} /> : <Volume2 size={15} />} Sound {muted ? "off" : "on"}
              </button>
            </div>
            <div className="relative grain rounded-3xl overflow-hidden p-8 text-white mb-8" style={{ background: "#F4A261" }}>
              <div className="flex items-center gap-2 text-white/85"><Gamepad2 size={18} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Mini-Games</span></div>
              <h1 className="font-head text-4xl md:text-5xl font-bold mt-2">Play & Learn</h1>
              <p className="mt-2 text-white/90 max-w-lg">Practice what you're learning with fun games built from your own lessons. Play one to earn the <b>Game On</b> badge! 🎮</p>
            </div>

            {!hasVocab && !hasQuiz ? (
              <div className="rounded-2xl border border-dashed border-[#EAE6DF] p-10 text-center text-[#6B7280]">
                <Sparkles size={22} className="mx-auto mb-2 text-[#F4A261]" />
                Finish a few lessons first — your games are built from your lesson words and questions!
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                <GameCard testid="game-match" disabled={!hasVocab} icon={Layers} color="#81B29A"
                  title="Vocabulary Match" desc="Match each word to its meaning as fast as you can."
                  onClick={() => setView("match")} note={hasVocab ? `${data.vocab.length} words ready` : "Need more lesson words"} />
                <GameCard testid="game-quiz" disabled={!hasQuiz} icon={Timer} color="#E07A5F"
                  title="Timed Quiz" desc="Beat the clock! Answer as many as you can before time runs out."
                  onClick={() => setView("quiz")} note={hasQuiz ? `${data.quiz.length} questions ready` : "Need more lesson questions"} />
                <GameCard testid="game-spelling" disabled={!hasSpelling} icon={SpellCheck} color="#3D405B"
                  title="Spelling Bee" desc="Hear the word and read its meaning, then type the correct spelling."
                  onClick={() => setView("spelling")} note={hasSpelling ? `${spellingPool.length} words ready` : "Need more lesson words"} />
                <GameCard testid="game-fillblank" disabled={!hasVocab} icon={PenLine} color="#F4A261"
                  title="Fill in the Blank" desc="Read the meaning and pick the word that fits."
                  onClick={() => setView("fillblank")} note={hasVocab ? `${data.vocab.length} words ready` : "Need more lesson words"} />
              </div>
            )}
          </>
        )}

        {data && view === "match" && <VocabMatch vocab={data.vocab} onExit={() => setView("hub")} />}
        {data && view === "quiz" && <TimedQuiz quiz={data.quiz} onExit={() => setView("hub")} />}
        {data && view === "spelling" && <SpellingBee vocab={spellingPool} onExit={() => setView("hub")} />}
        {data && view === "fillblank" && <FillBlank vocab={data.vocab} onExit={() => setView("hub")} />}
      </main>
    </div>
  );
}

function GameCard({ testid, icon: Icon, color, title, desc, note, onClick, disabled }) {
  return (
    <button data-testid={testid} onClick={disabled ? undefined : onClick} disabled={disabled}
      className={`text-left rounded-2xl bg-white border border-[#EAE6DF] p-6 transition-[transform,box-shadow] ${disabled ? "opacity-50 cursor-not-allowed" : "hover:-translate-y-1 hover:shadow-md"}`}>
      <span className="grid place-items-center w-12 h-12 rounded-2xl text-white" style={{ background: color }}><Icon size={22} /></span>
      <p className="font-head font-semibold text-lg mt-3 text-[#3D405B]">{title}</p>
      <p className="text-sm text-[#6B7280] mt-0.5">{desc}</p>
      <p className="text-xs font-semibold mt-3" style={{ color }}>{note}</p>
    </button>
  );
}

/* ---------- Vocabulary Match ---------- */
function VocabMatch({ vocab, onExit }) {
  const [round, setRound] = useState(0);
  const pairs = useRef(pick(vocab, Math.min(5, vocab.length)));
  const terms = useRef(shuffle(pairs.current.map((p, i) => ({ ...p, key: i }))));
  const defs = useRef(shuffle(pairs.current.map((p, i) => ({ ...p, key: i }))));
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState([]);
  const [wrong, setWrong] = useState(null);
  const [won, setWon] = useState(false);

  const reset = () => {
    pairs.current = pick(vocab, Math.min(5, vocab.length));
    terms.current = shuffle(pairs.current.map((p, i) => ({ ...p, key: i })));
    defs.current = shuffle(pairs.current.map((p, i) => ({ ...p, key: i })));
    setSelected(null); setMatched([]); setWrong(null); setWon(false); setRound((r) => r + 1);
  };

  const clickDef = (d) => {
    if (selected == null || matched.includes(d.key)) return;
    if (d.key === selected) {
      const nm = [...matched, d.key];
      setMatched(nm); setSelected(null);
      if (nm.length === pairs.current.length) {
        setWon(true); playWin();
        toast.success("You matched them all! 🎉 Badge unlocked: Game On");
      } else {
        playCorrect();
      }
    } else {
      setWrong(d.key); playWrong(); setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <div key={round}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-head text-2xl font-bold text-[#3D405B] flex items-center gap-2"><Layers size={20} className="text-[#81B29A]" /> Vocabulary Match</h2>
        <span className="stat text-sm text-[#6B7280]">{matched.length}/{pairs.current.length} matched</span>
      </div>

      {won ? (
        <Win score={pairs.current.length} total={pairs.current.length} color="#81B29A" onReplay={reset} onExit={onExit} />
      ) : (
        <>
          <p className="text-sm text-[#6B7280] mb-4">Tap a word, then tap its meaning.</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-3">
              {terms.current.map((t) => {
                const done = matched.includes(t.key);
                const sel = selected === t.key;
                return (
                  <button key={t.key} data-testid={`match-term-${t.key}`} disabled={done} onClick={() => setSelected(t.key)}
                    className={`w-full rounded-xl border px-4 py-3 text-left font-medium transition-colors ${done ? "bg-[#81B29A]/15 border-[#81B29A]/40 text-[#81B29A] line-through" : sel ? "border-[#81B29A] bg-[#81B29A]/10 text-[#3D405B]" : "border-[#EAE6DF] bg-white text-[#3D405B] hover:border-[#81B29A]"}`}>
                    {t.term}
                  </button>
                );
              })}
            </div>
            <div className="space-y-3">
              {defs.current.map((d) => {
                const done = matched.includes(d.key);
                const isWrong = wrong === d.key;
                return (
                  <motion.button key={d.key} data-testid={`match-def-${d.key}`} disabled={done} onClick={() => clickDef(d)}
                    animate={isWrong ? { x: [0, -6, 6, -6, 6, 0] } : {}} transition={{ duration: 0.4 }}
                    className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition-colors ${done ? "bg-[#81B29A]/15 border-[#81B29A]/40 text-[#81B29A]" : isWrong ? "border-[#E07A5F] bg-[#E07A5F]/10" : "border-[#EAE6DF] bg-white text-[#3D405B] hover:border-[#81B29A]"}`}>
                    {d.definition}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Timed Quiz ---------- */
const QUIZ_TIME = 45;
function TimedQuiz({ quiz, onExit }) {
  const [round, setRound] = useState(0);
  const questions = useRef(pick(quiz, Math.min(10, quiz.length)));
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(QUIZ_TIME);
  const [finished, setFinished] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (finished) return;
    if (time <= 0) { finish(score); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time, finished]);

  const finish = (s) => { if (!finished) { setFinished(true); playWin(); toast.success("Nice game! 🎮 Badge unlocked: Game On"); } };

  const answer = (opt) => {
    if (picked != null) return;
    const correct = opt === questions.current[i].correct_answer;
    setPicked(opt);
    const ns = correct ? score + 1 : score;
    if (correct) { setScore(ns); playCorrect(); } else { playWrong(); }
    setTimeout(() => {
      setPicked(null);
      if (i + 1 >= questions.current.length) finish(ns);
      else setI(i + 1);
    }, 650);
  };

  const reset = () => {
    questions.current = pick(quiz, Math.min(10, quiz.length));
    setI(0); setScore(0); setTime(QUIZ_TIME); setFinished(false); setPicked(null); setRound((r) => r + 1);
  };

  const q = questions.current[i];

  return (
    <div key={round}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-head text-2xl font-bold text-[#3D405B] flex items-center gap-2"><Timer size={20} className="text-[#E07A5F]" /> Timed Quiz</h2>
        {!finished && (
          <span data-testid="quiz-timer" className={`stat text-lg font-bold px-3 py-1 rounded-full ${time <= 10 ? "text-white bg-[#E07A5F]" : "text-[#3D405B] bg-[#F2EFE9]"}`}>{time}s</span>
        )}
      </div>

      {finished ? (
        <Win score={score} total={questions.current.length} color="#E07A5F" onReplay={reset} onExit={onExit} />
      ) : (
        <>
          <div className="flex items-center justify-between mb-4">
            <span className="stat text-sm text-[#6B7280]">Question {i + 1} of {questions.current.length}</span>
            <span className="stat text-sm text-[#81B29A] font-semibold">Score {score}</span>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="rounded-2xl bg-white border border-[#EAE6DF] p-7">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[#81B29A]">{q.subject}</p>
              <p className="font-head text-xl font-semibold text-[#3D405B] mt-1">{q.question}</p>
              <div className="mt-5 space-y-3">
                {q.options.map((opt, k) => {
                  let cls = "border-[#EAE6DF] hover:border-[#E07A5F]";
                  if (picked != null) {
                    if (opt === q.correct_answer) cls = "border-[#81B29A] bg-[#81B29A]/10";
                    else if (opt === picked) cls = "border-[#E07A5F] bg-[#E07A5F]/10";
                  }
                  return (
                    <button key={k} data-testid={`quiz-option-${k}`} onClick={() => answer(opt)}
                      className={`w-full text-left rounded-xl border px-4 py-3 text-[#3D405B] transition-colors ${cls}`}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </div>
  );
}

/* ---------- Spelling Bee ---------- */
function SpellingBee({ vocab, onExit }) {
  const [round, setRound] = useState(0);
  const words = useRef(pick(vocab, Math.min(6, vocab.length)));
  const [i, setI] = useState(0);
  const [val, setVal] = useState("");
  const [checked, setChecked] = useState(null); // null | 'right' | 'wrong'
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const w = words.current[i];

  const check = () => {
    if (checked) return;
    const right = val.trim().toLowerCase() === w.term.trim().toLowerCase();
    setChecked(right ? "right" : "wrong");
    if (right) { setScore((s) => s + 1); playCorrect(); } else { playWrong(); }
    setTimeout(() => {
      setChecked(null); setVal("");
      if (i + 1 >= words.current.length) { setDone(true); playWin(); }
      else setI(i + 1);
    }, right ? 800 : 1600);
  };

  const reset = () => {
    words.current = pick(vocab, Math.min(6, vocab.length));
    setI(0); setVal(""); setChecked(null); setScore(0); setDone(false); setRound((r) => r + 1);
  };

  return (
    <div key={round}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-head text-2xl font-bold text-[#3D405B] flex items-center gap-2"><SpellCheck size={20} className="text-[#3D405B]" /> Spelling Bee</h2>
        <span className="stat text-sm text-[#6B7280]">{i + (done ? 0 : 1)}{!done && `/${words.current.length}`} · Score {score}</span>
      </div>
      {done ? (
        <Win score={score} total={words.current.length} color="#3D405B" onReplay={reset} onExit={onExit} />
      ) : (
        <div className="rounded-2xl bg-white border border-[#EAE6DF] p-7">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#81B29A]">{w.subject} · Spell the word</p>
          <p className="text-[#3D405B] mt-2 leading-relaxed"><b>Clue:</b> {w.definition}</p>
          <div className="mt-3"><ReadAloudButton testid="spelling-hear" label="Hear the word" text={w.term} tone="#3D405B" /></div>
          <input data-testid="spelling-input" value={val} autoFocus onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()}
            placeholder="Type the word…"
            className={`mt-5 w-full rounded-xl border px-4 py-3 outline-none focus:ring-2 ${checked === "right" ? "border-[#81B29A] bg-[#81B29A]/10 focus:ring-[#81B29A]" : checked === "wrong" ? "border-[#E07A5F] bg-[#E07A5F]/10 focus:ring-[#E07A5F]" : "border-[#EAE6DF] focus:ring-[#3D405B]"}`} />
          {checked === "wrong" && <p className="mt-2 text-sm text-[#E07A5F]">The correct spelling is: <b>{w.term}</b></p>}
          {checked === "right" && <p className="mt-2 text-sm text-[#81B29A]">Correct! 🎉</p>}
          <button data-testid="spelling-check" onClick={check} disabled={!!checked}
            className="mt-5 flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105 disabled:opacity-60">
            <Send size={16} /> Check
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Fill in the Blank ---------- */
function FillBlank({ vocab, onExit }) {
  const [round, setRound] = useState(0);
  const build = () => {
    const chosen = pick(vocab, Math.min(6, vocab.length));
    return chosen.map((c) => {
      const distractors = pick(vocab.filter((v) => v.term !== c.term), 3).map((v) => v.term);
      return { ...c, options: shuffle([c.term, ...distractors]) };
    });
  };
  const items = useRef(build());
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = items.current[i];

  const answer = (opt) => {
    if (picked != null) return;
    setPicked(opt);
    if (opt === q.term) { setScore((s) => s + 1); playCorrect(); } else { playWrong(); }
    setTimeout(() => {
      setPicked(null);
      if (i + 1 >= items.current.length) { setDone(true); playWin(); }
      else setI(i + 1);
    }, 750);
  };

  const reset = () => { items.current = build(); setI(0); setPicked(null); setScore(0); setDone(false); setRound((r) => r + 1); };

  return (
    <div key={round}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-head text-2xl font-bold text-[#3D405B] flex items-center gap-2"><PenLine size={20} className="text-[#F4A261]" /> Fill in the Blank</h2>
        <span className="stat text-sm text-[#6B7280]">{done ? "" : `Question ${i + 1}/${items.current.length} · `}Score {score}</span>
      </div>
      {done ? (
        <Win score={score} total={items.current.length} color="#F4A261" onReplay={reset} onExit={onExit} />
      ) : (
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="rounded-2xl bg-white border border-[#EAE6DF] p-7">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[#81B29A]">{q.subject}</p>
            <p className="font-head text-xl font-semibold text-[#3D405B] mt-1">Which word means:</p>
            <p className="text-[#3D405B] mt-1 leading-relaxed">"{q.definition}"</p>
            <div className="mt-5 space-y-3">
              {q.options.map((opt, k) => {
                let cls = "border-[#EAE6DF] hover:border-[#F4A261]";
                if (picked != null) {
                  if (opt === q.term) cls = "border-[#81B29A] bg-[#81B29A]/10";
                  else if (opt === picked) cls = "border-[#E07A5F] bg-[#E07A5F]/10";
                }
                return (
                  <button key={k} data-testid={`fillblank-option-${k}`} onClick={() => answer(opt)}
                    className={`w-full text-left rounded-xl border px-4 py-3 font-medium text-[#3D405B] transition-colors ${cls}`}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

function Win({ score, total, color, onReplay, onExit }) {
  const perfect = score === total;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="game-result" className="text-center py-6">
      <div className="inline-grid place-items-center w-20 h-20 rounded-full text-white mb-4" style={{ background: color }}><Trophy size={34} /></div>
      <h2 className="font-head text-3xl font-bold text-[#3D405B]">{perfect ? "Perfect! 🌟" : "Great job! 🎉"}</h2>
      <p className="text-[#6B7280] mt-1">You got <span className="stat font-semibold text-[#3D405B]">{score} / {total}</span></p>
      <div className="flex items-center justify-center gap-3 mt-6">
        <button data-testid="game-replay" onClick={onReplay} className="flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: color }}>
          <RotateCw size={16} /> Play again
        </button>
        <button data-testid="game-exit" onClick={onExit} className="flex items-center gap-2 rounded-full border border-[#EAE6DF] px-6 py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors">
          <ChevronLeft size={16} /> More games
        </button>
      </div>
    </motion.div>
  );
}
